import {useRouter} from 'next/router';
import PropTypes from 'prop-types';
import {useCallback, useContext, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {RESPONSE_STATUSES} from '@/src/app/constants/api';
import {ROLES_NAMES} from '@/src/app/constants/roles';
import {
  selectDeleteAccountStatus,
  setDeleteAccountStatus,
} from '@/src/app/store/user/deleteAccountSlice';
import useUserActions from '@/src/app/store/user/useUserActions';
import useRoutePaths from '@/src/hooks/useRoutePaths';
import PopupContext from '@/src/popup/utils/PopupContext';
import fetchUserAction from '@/src/signUp/paymentDetails/utils/fetchUserAction';

import AccountSettingsWidgetLayout from '../components/AccountSettingsWidgetLayout';
import {PROFILE_MARKETING_STATUSES} from '../constants/profileMarketingStatuses';
import activateMarketingSubscription from '../utils/activateMarketingSubscription';
import deactivateMarketingSubscription from '../utils/deactivateMarketingSubscription';
import fetchDeleteAccountStatus from '../utils/fetchDeleteAccountStatus';
import ChangePasswordPopup from './ChangePasswordPopup';
import DeleteAccountPopup from './DeleteAccountPopup';

const AccountSettingsWidget = ({userType}) => {
  const {openPopup} = useContext(PopupContext);
  const dispatch = useDispatch();
  const {push} = useRouter();
  const deleteAccountStatus = useSelector(selectDeleteAccountStatus);
  const {
    user: {
      profile: {marketing_subscription_status: marketingSubscriptionStatus},
    },
    setUserData,
  } = useUserActions();
  const {SIGN_IN_ROUTE} = useRoutePaths();

  const handleChangePasswordClick = useCallback(() => {
    // @ts-ignore
    openPopup({
      popup: <ChangePasswordPopup />,
      options: {withCloseButton: true},
    });
  }, []);

  const fetchUserData = useCallback(async () => {
    const {status: fetchUserStatus, responseBody} = await fetchUserAction();

    if (fetchUserStatus === RESPONSE_STATUSES.SUCCESS) {
      setUserData(responseBody);
    } else {
      push(SIGN_IN_ROUTE.getLink());
    }
  }, [push]);

  const handleDeleteAccountClick = useCallback(() => {
    // @ts-ignore
    openPopup({
      popup: <DeleteAccountPopup />,
      options: {withCloseButton: true},
    });
  }, []);

  const handleEmailSubscriptionButtonClick = useCallback(async () => {
    if (marketingSubscriptionStatus !== PROFILE_MARKETING_STATUSES.SUBSCRIBED) {
      const {status} = await activateMarketingSubscription();

      if (status === RESPONSE_STATUSES.SUCCESS) {
        fetchUserData();
      }
    } else {
      const {status} = await deactivateMarketingSubscription();

      if (status === RESPONSE_STATUSES.SUCCESS) {
        fetchUserData();
      }
    }
  }, [marketingSubscriptionStatus, fetchUserData]);

  useEffect(() => {
    const fetchDeleteData = async () => {
      const {status, responseBody} = await fetchDeleteAccountStatus();

      if (status === RESPONSE_STATUSES.SUCCESS) {
        dispatch(
          setDeleteAccountStatus(
            responseBody.delete_account_requests.length > 0
          )
        );
      }
    };

    if (deleteAccountStatus.isPending === null) {
      fetchDeleteData();
    }
  }, [deleteAccountStatus]);

  return (
    <AccountSettingsWidgetLayout
      onChangePasswordClick={handleChangePasswordClick}
      onDeleteAccountClick={handleDeleteAccountClick}
      marketingSubscriptionStatus={marketingSubscriptionStatus}
      onEmailSubscriptionButtonClick={handleEmailSubscriptionButtonClick}
      withBecomeCreatorBlock={
        userType !== ROLES_NAMES.COURSE_CREATOR &&
        userType !== ROLES_NAMES.CLINIC
      }
      isDeleteAccountPending={deleteAccountStatus.isPending || false}
      withTopMargin={userType !== ROLES_NAMES.CLINIC}
    />
  );
};

AccountSettingsWidget.propTypes = {
  isEmailSubscriptionActive: PropTypes.bool.isRequired,
  userType: PropTypes.string.isRequired,
};

export default AccountSettingsWidget;
